import { useEffect, useMemo, useState } from 'react';
import {
  Calendar,
  Download,
  GitBranchPlus,
  PhoneMissed,
  RefreshCw,
  Search,
  Users,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { Input } from '@/components/ui/Input';
import { fetchQueues, fetchFailoverFollowThrough } from '@/services/api';
import type {
  FailoverFollowThroughReport as ReportData,
  FailoverLegOutcome,
  Queue,
} from '@/types';
import { getPresetHtmlDateRange } from '@/utils/helpers';
import { formatFailoverOutcomeLabel } from '@/lib/failoverCorrelation';
import { ContactNameLine } from '@/components/crm/CallerName';
import { useModuleAccess } from '@/lib/useModuleAccess';
import { loadPdfAssets } from '@/lib/pdf/pdfAssets';
import { describeFilters } from '@/lib/reports/schedule';
import { failoverCsv, failoverPdf, filterFailoverRows } from '@/lib/reports/failoverReport';
import { downloadText } from './download';
import { ScheduleReportButton } from './ScheduleReportButton';

type DatePreset = 'today' | 'week' | 'month' | 'custom';

function defaultDateRange() {
  return getPresetHtmlDateRange('today');
}

function toYeastarDateTime(date: string, endOfDay: boolean) {
  return `${date.replace(/-/g, '/')} ${endOfDay ? '23:59:59' : '00:00:00'}`;
}

function formatDuration(seconds: number): string {
  if (!seconds || seconds <= 0) return '0s';
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  if (m === 0) return `${s}s`;
  return s ? `${m}m ${s}s` : `${m}m`;
}

function outcomeBadgeVariant(
  outcome: FailoverLegOutcome
): 'success' | 'danger' | 'warning' | 'default' {
  if (outcome === 'answered') return 'success';
  if (outcome === 'no_answer' || outcome === 'busy' || outcome === 'no_leg')
    return 'danger';
  if (outcome === 'voicemail') return 'warning';
  return 'default';
}

function StatCard({
  label,
  value,
  hint,
  tone = 'default',
}: {
  label: string;
  value: number | string;
  hint?: string;
  tone?: 'default' | 'red' | 'green' | 'amber';
}) {
  const tones = {
    default: 'bg-gray-50 dark:bg-gray-800/60 border-gray-200 dark:border-gray-700',
    red: 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800',
    green: 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800',
    amber: 'bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800',
  };
  return (
    <div className={`rounded-xl border p-4 ${tones[tone]}`}>
      <div className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
        {label}
      </div>
      <div className="mt-1 text-2xl font-semibold text-gray-900 dark:text-white">
        {value}
      </div>
      {hint ? (
        <div className="mt-1 text-xs text-gray-500 dark:text-gray-400">{hint}</div>
      ) : null}
    </div>
  );
}

export function FailoverFollowThroughReport() {
  const { session } = useModuleAccess('reporting');
  const range = defaultDateRange();
  const [startDate, setStartDate] = useState(range.startDate);
  const [endDate, setEndDate] = useState(range.endDate);
  const [preset, setPreset] = useState<DatePreset>('today');
  const [queues, setQueues] = useState<Queue[]>([]);
  const [selectedQueueIds, setSelectedQueueIds] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingQueues, setLoadingQueues] = useState(true);
  const [report, setReport] = useState<ReportData | null>(null);
  const [search, setSearch] = useState('');
  const [outcomeFilter, setOutcomeFilter] = useState<string>('all');

  useEffect(() => {
    (async () => {
      try {
        setLoadingQueues(true);
        const list = await fetchQueues();
        setQueues(list);
      } catch (e: any) {
        toast.error(e?.message || 'Failed to load queues');
      } finally {
        setLoadingQueues(false);
      }
    })();
  }, []);

  const applyPreset = (p: DatePreset) => {
    setPreset(p);
    if (p === 'custom') return;
    const r = getPresetHtmlDateRange(p);
    setStartDate(r.startDate);
    setEndDate(r.endDate);
  };

  const toggleQueue = (id: string) => {
    setSelectedQueueIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const generate = async () => {
    if (!startDate || !endDate) {
      toast.error('Select a date range');
      return;
    }
    setLoading(true);
    try {
      const data = await fetchFailoverFollowThrough({
        startTime: toYeastarDateTime(startDate, false),
        endTime: toYeastarDateTime(endDate, true),
        queueIds: selectedQueueIds.length ? selectedQueueIds : undefined,
        abandonTime: 3,
      });
      setReport(data);
      toast.success(
        `${data.rows.length} queue miss${data.rows.length === 1 ? '' : 'es'} · ${data.summary.failoverAnswered} failover answered · ${data.summary.failoverNoLeg} no CDR leg`
      );
    } catch (e: any) {
      console.error(e);
      toast.error(e?.message || 'Failed to build failover follow-through report');
    } finally {
      setLoading(false);
    }
  };

  const filteredRows = useMemo(
    () => (report ? filterFailoverRows(report.rows, outcomeFilter, search) : []),
    [report, search, outcomeFilter]
  );

  const scheduleFilters = { queueIds: selectedQueueIds, outcome: outcomeFilter, search: search.trim() || undefined };

  const exportPdf = async () => {
    if (!report) return;
    try {
      const names = new Map(queues.map((q) => [q.id, q.name || q.number || q.id]));
      failoverPdf({
        report,
        rows: filteredRows,
        filtersText: describeFilters('failover', scheduleFilters, { queues: names }),
        startDate,
        endDate,
        customerName: session?.customer?.name,
        assets: await loadPdfAssets(),
      }).save(`failover-followthrough-${startDate}-to-${endDate}.pdf`);
    } catch (error) {
      console.error(error);
      toast.error('Could not make the PDF');
    }
  };

  const agentMissCounts = useMemo(() => {
    if (!report) return [] as Array<{ label: string; count: number }>;
    const map = new Map<string, number>();
    for (const m of report.agentMisses) {
      const label = m.agent_name
        ? `${m.agent_name} (${m.agent_num})`
        : m.agent_num || 'Unknown';
      map.set(label, (map.get(label) || 0) + 1);
    }
    return [...map.entries()]
      .map(([label, count]) => ({ label, count }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 12);
  }, [report]);

  const s = report?.summary;

  return (
    <div className="space-y-6">
      <Card>
        <div className="flex items-center gap-3 mb-6">
          <div className="p-3 bg-violet-100 dark:bg-violet-900/30 rounded-xl">
            <GitBranchPlus className="w-6 h-6 text-violet-600 dark:text-violet-400" />
          </div>
          <div>
            <h2 className="text-2xl font-semibold text-gray-900 dark:text-white">
              Failover Follow-through
            </h2>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Queue “missed” only means no queue agent answered. This report joins the
              post-timeout failover leg (did the phone ring / answer / busy?) and who was
              offered the call in the queue.
            </p>
          </div>
        </div>

        <div className="flex flex-wrap gap-2 mb-4">
          {(['today', 'week', 'month', 'custom'] as DatePreset[]).map((p) => (
            <button
              key={p}
              type="button"
              onClick={() => applyPreset(p)}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium ${
                preset === p
                  ? 'bg-violet-600 text-white'
                  : 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300'
              }`}
            >
              {p === 'today'
                ? 'Today'
                : p === 'week'
                  ? 'This week'
                  : p === 'month'
                    ? 'This month'
                    : 'Custom'}
            </button>
          ))}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Start
            </label>
            <Input
              type="date"
              className="dark:[color-scheme:dark]"
              value={startDate}
              onChange={(e) => {
                setPreset('custom');
                setStartDate(e.target.value);
              }}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              End
            </label>
            <Input
              type="date"
              className="dark:[color-scheme:dark]"
              value={endDate}
              onChange={(e) => {
                setPreset('custom');
                setEndDate(e.target.value);
              }}
            />
          </div>
          <div className="flex items-end gap-2">
            <Button
              onClick={generate}
              disabled={loading || loadingQueues}
              className="w-full inline-flex items-center justify-center gap-2"
            >
              {loading ? (
                <RefreshCw className="w-4 h-4 animate-spin" />
              ) : (
                <RefreshCw className="w-4 h-4" />
              )}
              <span>{loading ? 'Correlating…' : 'Generate'}</span>
            </Button>
          </div>
        </div>

        <div className="mb-2 text-sm font-medium text-gray-700 dark:text-gray-300">
          Queues (empty = all)
        </div>
        {loadingQueues ? (
          <div className="h-20 bg-gray-100 dark:bg-gray-800 rounded-lg animate-pulse" />
        ) : (
          <div className="flex flex-wrap gap-2 max-h-36 overflow-y-auto">
            {queues.map((q) => {
              const active = selectedQueueIds.includes(q.id);
              return (
                <button
                  key={q.id}
                  type="button"
                  onClick={() => toggleQueue(q.id)}
                  className={`px-2.5 py-1 rounded-full text-xs font-medium border ${
                    active
                      ? 'bg-violet-600 text-white border-violet-600'
                      : 'bg-white dark:bg-gray-900 text-gray-700 dark:text-gray-300 border-gray-200 dark:border-gray-700'
                  }`}
                  title={q.number}
                >
                  {q.name}
                  {q.number ? ` (${q.number})` : ''}
                </button>
              );
            })}
          </div>
        )}
      </Card>

      {report && s ? (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
            <StatCard label="Queue misses" value={s.totalQueueMisses} tone="default" />
            <StatCard
              label="Timed out (failover should fire)"
              value={s.timedOut}
              tone="amber"
            />
            <StatCard
              label="Failover answered"
              value={s.failoverAnswered}
              hint="Queue still shows missed — correctly"
              tone="green"
            />
            <StatCard label="Rang, no answer" value={s.failoverNoAnswer} tone="red" />
            <StatCard label="No CDR leg found" value={s.failoverNoLeg} tone="red" />
            <StatCard
              label="Busy during overflow"
              value={s.busyDuringCount}
              tone="amber"
            />
          </div>

          <Card>
            <div className="flex flex-wrap items-center gap-3 mb-4">
              <div className="relative flex-1 min-w-[200px]">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <Input
                  className="pl-9"
                  placeholder="Search caller, queue, agent, failover…"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </div>
              <select
                className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 px-3 py-2 text-sm text-gray-900 dark:text-gray-100 dark:[color-scheme:dark]"
                value={outcomeFilter}
                onChange={(e) => setOutcomeFilter(e.target.value)}
              >
                <option value="all">All failover outcomes</option>
                <option value="answered">Failover answered</option>
                <option value="no_answer">Rang, no answer</option>
                <option value="busy">Busy</option>
                <option value="no_leg">No CDR leg</option>
                <option value="voicemail">Voicemail</option>
                <option value="not_applicable">N/A</option>
              </select>
              <Button
                variant="secondary"
                onClick={() =>
                  downloadText(
                    failoverCsv(filteredRows),
                    `failover-followthrough-${startDate}-to-${endDate}.csv`,
                    'text/csv;charset=utf-8'
                  )
                }
                disabled={filteredRows.length === 0}
              >
                <Download className="w-4 h-4 mr-2" />
                CSV
              </Button>
              <Button variant="secondary" onClick={exportPdf} disabled={filteredRows.length === 0}>
                <Download className="w-4 h-4 mr-2" />
                PDF
              </Button>
              <ScheduleReportButton
                kind="failover"
                filters={scheduleFilters}
                dateRange={preset === 'today' ? 'today' : preset === 'week' ? 'thisWeek' : preset === 'month' ? 'thisMonth' : undefined}
              />
            </div>

            <div className="overflow-x-auto">
              <table className="min-w-full text-sm text-gray-700 dark:text-gray-200">
                <thead>
                  <tr className="text-left text-xs uppercase text-gray-500 dark:text-gray-400 border-b border-gray-200 dark:border-gray-700">
                    <th className="py-2 pr-3">Time</th>
                    <th className="py-2 pr-3">Caller</th>
                    <th className="py-2 pr-3">Queue</th>
                    <th className="py-2 pr-3">Wait</th>
                    <th className="py-2 pr-3">Agents rung</th>
                    <th className="py-2 pr-3">Failover</th>
                    <th className="py-2 pr-3">Outcome</th>
                    <th className="py-2">What happened</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredRows.length === 0 ? (
                    <tr>
                      <td
                        colSpan={8}
                        className="py-8 text-center text-gray-500 dark:text-gray-400"
                      >
                        No rows match this filter.
                      </td>
                    </tr>
                  ) : (
                    filteredRows.map((row) => (
                      <tr
                        key={row.id}
                        className="border-b border-gray-100 dark:border-gray-800 align-top"
                      >
                        <td className="py-2.5 pr-3 whitespace-nowrap font-mono text-xs">
                          {row.time}
                        </td>
                        <td className="py-2.5 pr-3 font-mono text-xs">
                          {row.call_from}
                          <ContactNameLine value={row.call_from} />
                        </td>
                        <td className="py-2.5 pr-3">
                          <div className="font-medium text-gray-900 dark:text-white">
                            {row.queue_name}
                          </div>
                          {row.queue_num ? (
                            <div className="text-xs text-gray-500 dark:text-gray-400">
                              {row.queue_num}
                            </div>
                          ) : null}
                        </td>
                        <td className="py-2.5 pr-3 whitespace-nowrap">
                          {formatDuration(row.wait_seconds)}
                          {row.timed_out ? (
                            <Badge variant="warning" className="ml-1">
                              timeout
                            </Badge>
                          ) : null}
                        </td>
                        <td className="py-2.5 pr-3 max-w-[180px]">
                          {row.agents_who_missed.length ? (
                            <div className="text-xs text-gray-700 dark:text-gray-300">
                              {row.agents_who_missed.join(', ')}
                            </div>
                          ) : (
                            <span className="text-xs text-gray-400">
                              {row.agents_in_queue.length
                                ? `Configured: ${row.agents_in_queue.length} agents`
                                : '—'}
                            </span>
                          )}
                        </td>
                        <td className="py-2.5 pr-3 text-xs">{row.failover_label}</td>
                        <td className="py-2.5 pr-3">
                          <Badge variant={outcomeBadgeVariant(row.failover_outcome)}>
                            {formatFailoverOutcomeLabel(row.failover_outcome)}
                          </Badge>
                          {row.busy_during_failover ? (
                            <div className="text-xs text-amber-600 dark:text-amber-400 mt-1">
                              Busy overlap
                            </div>
                          ) : null}
                        </td>
                        <td className="py-2.5 text-xs text-gray-600 dark:text-gray-300 max-w-md">
                          {row.what_happened}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </Card>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <Card>
              <div className="flex items-center gap-2 mb-3">
                <PhoneMissed className="w-4 h-4 text-red-500" />
                <h3 className="font-semibold text-gray-900 dark:text-white">
                  Agents who missed (activity)
                </h3>
              </div>
              {agentMissCounts.length === 0 ? (
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  No agent-missed activity returned for this period (agents may have been
                  logged out, or the PBX report is empty).
                </p>
              ) : (
                <ul className="space-y-1.5 text-sm text-gray-700 dark:text-gray-200">
                  {agentMissCounts.map((a) => (
                    <li
                      key={a.label}
                      className="flex justify-between gap-2 border-b border-gray-100 dark:border-gray-800 py-1"
                    >
                      <span>{a.label}</span>
                      <span className="font-mono font-medium">{a.count}</span>
                    </li>
                  ))}
                </ul>
              )}
            </Card>

            <Card>
              <div className="flex items-center gap-2 mb-3">
                <Users className="w-4 h-4 text-primary-500" />
                <h3 className="font-semibold text-gray-900 dark:text-white">
                  Agent logins
                </h3>
              </div>
              {report.agentLogins.length === 0 ? (
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  No login activity (static agents often do not produce login rows).
                </p>
              ) : (
                <ul className="space-y-1.5 text-sm text-gray-700 dark:text-gray-200 max-h-64 overflow-y-auto">
                  {report.agentLogins.slice(0, 40).map((l, i) => (
                    <li
                      key={`${l.agent_number}-${l.login}-${i}`}
                      className="text-xs border-b border-gray-100 dark:border-gray-800 py-1"
                    >
                      <span className="font-medium">
                        {l.agent_name || l.agent_number}
                        {l.agent_name ? ` (${l.agent_number})` : ''}
                      </span>
                      <div className="text-gray-500 dark:text-gray-400">
                        in {l.login}
                        {l.logout ? ` → out ${l.logout}` : ''}
                        {l.total_login_time != null
                          ? ` · ${formatDuration(l.total_login_time)}`
                          : ''}
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </Card>

            <Card>
              <div className="flex items-center gap-2 mb-3">
                <Calendar className="w-4 h-4 text-amber-500" />
                <h3 className="font-semibold text-gray-900 dark:text-white">
                  Agent pauses
                </h3>
              </div>
              {report.agentPauses.length === 0 ? (
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  No pause activity for this period.
                </p>
              ) : (
                <ul className="space-y-1.5 text-sm text-gray-700 dark:text-gray-200 max-h-64 overflow-y-auto">
                  {report.agentPauses.slice(0, 40).map((p, i) => (
                    <li
                      key={`${p.agent_number}-${p.pause}-${i}`}
                      className="text-xs border-b border-gray-100 dark:border-gray-800 py-1"
                    >
                      <span className="font-medium">
                        {p.agent_name || p.agent_number}
                        {p.agent_name ? ` (${p.agent_number})` : ''}
                      </span>
                      <div className="text-gray-500 dark:text-gray-400">
                        {p.pause}
                        {p.unpause ? ` → ${p.unpause}` : ''}
                        {p.total_pause_time != null
                          ? ` · ${formatDuration(p.total_pause_time)}`
                          : ''}
                        {p.reason ? ` · ${p.reason}` : ''}
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </Card>
          </div>
        </>
      ) : null}
    </div>
  );
}
